import { FileUpload } from '../../flightUpload/FileUpload';
import { stylesheet } from 'typestyle';
import { Alert, Gray100, typeCompact14RegularProperties } from '@traceair/tds';
import { InfoOutlineIcon } from '@traceair/webapp-icons';
import { ProjectData } from '../../../types';
import { GcpRequestData } from '../../../../../../store/uploadStore';
import { useAppStore } from '../../../../../../store/appStore';
import { useHandleGcpFileUpload } from './useHandleGcpFileUpload';
import { isMissingRinexArchive } from './isMissingRinexArchive';
import { GcpRequestUploadProgress } from '../gcpRequestProgress/GcpRequestUploadProgress';
import { GcpRequestFileType } from '../gcpRequestFileType';

const ACCEPT = {
  [GcpRequestFileType.Measurement]: {
    'application/pdf': ['.pdf'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.oasis.opendocument.spreadsheet': ['.ods'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/csv': ['.csv'],
    'text/plain': ['.txt', '.log'],
    'text/tab-separated-values': ['.tsv'],
    'application/zip': ['.zip'],
  },
  [GcpRequestFileType.Photo]: {
    'image/*': [],
  },
};

const TEXTS = {
  measurement: {
    heading: 'GCP Coordinates',
    title: 'Drop measurements here, or',
    description: 'Preferred format: CSV',
  },
  measurementWithRinex: {
    heading: 'GCP Coordinates and RINEX',
    title: 'Drop files here, or',
    description: 'CSV for coordinates. ZIP with base station RINEX files.',
  },
  photo: {
    heading: 'GCPs photos (optional)',
    title: 'Drop photos or folders here, or',
    description: 'Preferred format: JPG',
  },
};

const css = stylesheet({
  container: {
    marginBottom: 24,
  },
  heading: {
    ...typeCompact14RegularProperties,
    marginBottom: 8,
    color: Gray100,
  },
  alert: {
    marginTop: 8,
  },
});

interface Props extends ProjectData {
  gcpRequest: GcpRequestData;
  type: GcpRequestFileType;
}

export const GcpRequestUploadFiles = ({ customer, site, gcpRequest, type }: Props) => {
  const isMeasurement = type === GcpRequestFileType.Measurement;
  const handleChange = useHandleGcpFileUpload(customer, site, gcpRequest, type);
  const { pilot } = useAppStore();

  // RINEX is only requested from pilots with a UAS Pilots AT record.
  const asksForRinex = isMeasurement && !!pilot?.id;
  const texts = asksForRinex ? TEXTS.measurementWithRinex : isMeasurement ? TEXTS.measurement : TEXTS.photo;

  const files = isMeasurement ? gcpRequest.measurements : gcpRequest.photos;
  const uploads = isMeasurement ? gcpRequest.measurementUploads : gcpRequest.photoUploads;

  return (
    <div className={css.container}>
      {(gcpRequest.meta.isEditMode || files.length > 0) && <div className={css.heading}>{texts.heading}</div>}

      {gcpRequest.meta.isEditMode && (
        <FileUpload
          accept={ACCEPT[type]}
          fileFormatIcons={isMeasurement ? ['CSV'] : ['JPG']}
          onChange={handleChange}
          title={texts.title}
          description={texts.description}
          dragOverText="Drop files or folders here"
          reduceHeight
          multiple
          hideIcons={Boolean(files.length || uploads.length)}
        />
      )}

      <GcpRequestUploadProgress customer={customer} site={site} gcpRequest={gcpRequest} type={type} />

      {asksForRinex && gcpRequest.meta.isEditMode && isMissingRinexArchive(gcpRequest) && (
        <Alert
          className={css.alert}
          type="neutral"
          icon={InfoOutlineIcon}
          title="No base station RINEX archive, add ZIP with files"
        />
      )}
    </div>
  );
};
